import BreadCrumbs from '@/designs/new/components/atom/breadCrumbs/breadCrumbs'
import Card from '@/designs/new/components/atom/card/card'
import Tabs from '@/components/atom/tabCmp/tab'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks/storehooks'
import {
  accName,
  calendar,
  CaseNumberIcon,
  hash,
  location,
} from '@/assets/svgIcons'
import { caseDetails } from '@/services/apis/api'
import { formatDefPayload } from '@/lib/utils'
import { useNavigate, useParams } from 'react-router-dom'
import { setCaseDetails } from '@/redux-store/slices/caseManagementSlice/caseManagementSlice'
import { AxiosResponse } from 'axios'
import { toast } from 'sonner'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { payloadDefaultProp } from '@/containers/header/dynamicDropdown'
import CaseDetailsForm from '@/designs/new/containers/CaseManagement/caseDetails/caseDetailsForm'
import CommentsSection from '../commentSection/commentSection'
import CaseFiles from './CaseFiles'
// import CaseHistory from '../caseHistory/caseHistory'
// import CreateCases from '../createCasesOld'

interface CaseDetailsProps {
  cases: Case[]
}

export type Case = {
  Acc_Name_Text__c: string
  CAD_Open_Code__c: string
  CaseNumber: string
  ClosedDate: string
  ContactEmail: string
  ContactMobile: string
  ContactPhone: string
  CreatedDate: string
  CreatedDate__c: string
  Description: string
  E2CP_AdditionalTo__c: string
  Id: string
  LastModifiedDate: string
  Provider__c: string
  Resolution_Date__c: string
  Service_Location__c: string
  Status: string
  Subject: string
  CAD_Status_Reason__c: string
  E2CP__AdditionalCC__c: string
  citsm__Resolution_Description__c: string
  CAD_Resolution_Code__c: string
} & Record<string, unknown>

export type caseDataType = {
  data: Case
}

type FetchCaseDetails = AxiosResponse<{ data: CaseDetailsProps }>
const CaseDetails = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const { caseId } = useParams()
  const defPayload = useAppSelector(
    state => state.menu.payloadDef,
  ) as payloadDefaultProp
  const perm = useAppSelector(state => state.menu.perms)

  const [casesObj, setCaseObj] = useState<Case>({
    Acc_Name_Text__c: '',
    CAD_Open_Code__c: '',
    CaseNumber: '',
    ClosedDate: '',
    ContactEmail: '',
    ContactMobile: '',
    ContactPhone: '',
    CreatedDate: '',
    CreatedDate__c: '',
    Description: '',
    E2CP_AdditionalTo__c: '',
    Id: '',
    LastModifiedDate: '',
    Provider__c: '',
    Resolution_Date__c: '',
    Service_Location__c: '',
    Status: '',
    Subject: '',
    CAD_Status_Reason__c: '',
    E2CP__AdditionalCC__c: '',
    citsm__Resolution_Description__c: '',
    CAD_Resolution_Code__c: '',
  })
  const [loading, setLoading] = useState(true)

  const isInitialRender = useRef(true)
  const prevPayload = useRef(defPayload)
  const navigate = useNavigate()
  const CallCaseDetail = useCallback(async () => {
    await Promise.resolve()
    setLoading(true)
    const res = await caseDetails({
      caseIds: [caseId?.toString()],
      caseFields: [
        'CaseNumber',
        'Acc_Name_Text__c',
        'Subject',
        'Status',
        'Service_Location__c',
        'LastModifiedDate',
        'CreatedDate',
        'ClosedDate',
        'CreatedDate__c',
        'Primary_Service__c',
        'Provider__c',
        'ContactPhone',
        'ContactMobile',
        'ContactEmail',
        'E2CP__AdditionalTo__c',
        'Resolution_Date__c',
        'Description',
        'CAD_Open_Code__c',
        'CAD_Status_Reason__c',
        'E2CP__AdditionalCC__c',
        'citsm__Resolution_Description__c',
        'CAD_Resolution_Code__c',
      ],
      ...formatDefPayload(defPayload),
    })

    const response = res as FetchCaseDetails

    setLoading(false)
    if (response.status === 200) {
      const result = response?.data?.data?.cases?.[0] ?? {}
      dispatch(setCaseDetails(result))
      setCaseObj(result as unknown as Case)
    } else {
      toast.error('Failed to fetch case details')
    }
  }, [caseId, defPayload, dispatch])

  const normalizeIds = (ids: string[] | 'all'): string[] =>
    ids === 'all' ? ['all'] : ids

  useEffect(() => {
    const isValid =
      defPayload.customerId && defPayload.siteId.length > 0 && caseId
    if (!isValid) return

    if (isInitialRender.current) {
      isInitialRender.current = false
      prevPayload.current = defPayload
      Promise.resolve().then(CallCaseDetail)
      return
    }

    const customerChanged =
      normalizeIds(prevPayload.current.customerId).join(',') !==
      normalizeIds(defPayload.customerId).join(',')

    const siteChanged =
      prevPayload.current.siteId.join(',') !== defPayload.siteId.join(',')

    if (customerChanged || siteChanged) {
      navigate('/support/casemanagement')
    } else {
      Promise.resolve().then(CallCaseDetail)
    }

    prevPayload.current = defPayload
  }, [defPayload, caseId, CallCaseDetail, navigate])

  // console.log('caseObj:', casesObj)

  const tabs = [
    {
      label: 'Details',
      content: (
        <div className='h-full pr-2'>
          {/* <CreateCases /> */}
          <CaseDetailsForm isViewOnly={true} casesObj={casesObj} />
        </div>
      ),
    },
  ]

  if (perm['support.casemanagement'].includes('cases.comment')) {
    tabs.push({
      label: 'Comments',
      content: (
        <div>
          <CommentsSection />
        </div>
      ),
    })
  }

  const formatDate = (date: string) => {
    if (!date) return '---'

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }
  return (
    <div className='flex gap-2 h-full w-full overflow-hidden'>
      <Card className='flex-1 min-w-0 h-full px-6 pt-3 pb-6 flex flex-col overflow-hidden'>
        <div className='shrink-0 flex flex-col'>
          <BreadCrumbs />
        </div>
        {loading ? (
          <TicketDetailsSkeleton />
        ) : (
          <div className='my-3 flex-1 min-h-0 flex flex-col'>
            <div className='flex flex-col p-2 gap-2 rounded-[0.75rem] border border-solid border-border bg-tertiary shrink-0'>
              {/* header1 */}
              <div className='flex py-2 px-3 items-center justify-between rounded-[0.625rem] border border-solid border-secondary bg-primary'>
                <div className='flex gap-2 items-center'>
                  <CaseNumberIcon />
                  <div className='flex flex-col'>
                    <p className='text-info-forground text-kpi-heading font-semibold'>
                      {casesObj?.CaseNumber}
                    </p>
                    <p className='text-infor-forground text-badge'>
                      Case Number
                    </p>
                  </div>
                </div>
                <div className='flex gap-2 items-center'>
                  <div className='px-2 py-0.5 flex justify-center items-center gap-1 rounded-full border border-solid border-info-border bg-info-dim-light'>
                    <p className='text-info-forground text-badge font-semibold'>
                      {casesObj?.Status}
                    </p>
                  </div>
                </div>
              </div>
              {/* header grid (4 columns) */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
                <div className='flex py-2 px-3 items-center gap-2 rounded-[0.625rem] bg-primary'>
                  {accName}
                  <div className='flex flex-col min-w-0'>
                    <p
                      className='text-primary-foreground text-kpi-heading font-semibold break-words'
                      title={casesObj?.Acc_Name_Text__c}
                    >
                      {casesObj?.Acc_Name_Text__c}
                    </p>
                    <p className='text-secondary-foreground text-badge'>
                      Account Name
                    </p>
                  </div>
                </div>
                <div className='flex py-2 px-3 items-center gap-2 rounded-[0.625rem] bg-primary'>
                  {calendar}
                  <div className='flex flex-col min-w-0'>
                    <p className='text-primary-foreground text-kpi-heading font-semibold break-words'>
                      {formatDate(casesObj?.CreatedDate__c)}
                    </p>
                    <p className='text-secondary-foreground text-badge'>
                      Created On
                    </p>
                  </div>
                </div>
                <div className='flex py-2 px-3 items-center gap-2 rounded-[0.625rem] bg-primary'>
                  {location}
                  <div className='flex flex-col min-w-0'>
                    <p
                      className='text-primary-foreground text-kpi-heading font-semibold break-words'
                      title={casesObj?.Service_Location__c}
                    >
                      {casesObj?.Service_Location__c}
                    </p>
                    <p className='text-secondary-foreground text-badge'>
                      Service Location
                    </p>
                  </div>
                </div>
                <div className='flex py-2 px-3 items-center gap-2 rounded-[0.625rem] bg-primary'>
                  {hash}
                  <div className='flex flex-col min-w-0'>
                    <p className='text-primary-foreground text-kpi-heading font-semibold break-words'>
                      {casesObj?.CAD_Open_Code__c || '---'}
                    </p>
                    <p className='text-secondary-foreground text-badge'>
                      Open Code
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* tabs */}
            <div className='mt-2 min-h-0 flex-1 flex flex-col'>
              <Tabs tabs={tabs} />
            </div>
          </div>
          // <div className='mb-3 h-[calc(100%-200px)]'>
          //     <div
          //         className='h-auto py-6 my-6 rounded-radius-md flex flex-row gap-16 bg-info-dim'
          //         data-testid='test-view-ticket-header'
          //     >
          //         <div className=' flex flex-row gap-10 w-full justify-evenly '>
          //             <div className='flex flex-row'>
          //                 <div className=' flex pt-1.5 px-4'>
          //                     <CaseIcon />
          //                 </div>
          //                 <div className='flex flex-col '>
          //                     <span className='text-primary-foreground text-lg font-normal'>
          //                         Case
          //                     </span>
          //                     <span className='text-primary-foreground  text-2xl font-bold'>
          //                         {casesObj?.CaseNumber}
          //                     </span>
          //                 </div>
          //             </div>
          //             <div className='border-r border-secondary-border h-[4.75rem] w-[1px]'></div>
          //             <div className='flex flex-col max-w-[200px]'>
          //                 <div className='text-lg font-normal text-primary-foreground '>
          //                     Priority
          //                 </div>
          //                 <div className='text-lg font-semibold text-primary-foreground '>
          //                     {casesObj?.Priority}
          //                 </div>
          //             </div>
          //             <div className='border-r border-secondary-border h-[4.75rem] w-[1px]'></div>

          //             <div className='flex flex-col max-w-full'>
          //                 <span className='text-lg font-normal text-primary-foreground '>
          //                     Account Name
          //                 </span>
          //                 <span
          //                     className='text-lg font-semibold text-primary-foreground '
          //                     data-testid='test-accountName'
          //                 >
          //                     {casesObj?.Acc_Name_Text__c}
          //                 </span>
          //             </div>
          //             <div className='border-r border-secondary-border h-[4.75rem] w-[1px]'></div>
          //             <div className='flex flex-col max-w-full justify-center'>
          //                 <span className=' font-normal text-lg text-primary-foreground'>
          //                     Service Location
          //                 </span>
          //                 <span
          //                     className='text-lg font-semibold text-primary-foreground'
          //                     data-testid='test-serviceLocation'
          //                 >
          //                     {casesObj?.Service_Location__c}
          //                 </span>
          //             </div>

          //             <div className='border-r border-secondary-border h-[4.75rem] w-[1px]'></div>

          //             <div className='flex flex-col'>
          //                 <span className=' text-lg font-normal text-primary-foreground '>
          //                     Open Code
          //                 </span>
          //                 <span
          //                     className='text-lg font-semibold text-primary-foreground '
          //                     data-testid='test-openCode'
          //                 >
          //                     {casesObj?.CAD_Open_Code__c || '---'}
          //                 </span>
          //             </div>
          //         </div>
          //     </div>
          //     <div className='h-[calc(100%-20px)] overflow-y-auto spec-scroll '>
          //         <Tabs tabs={tabs} />
          //     </div>
          // </div>
        )}
      </Card>
      {!loading && <CaseFiles caseId={casesObj?.Id} />}
    </div>
  )
}

export default CaseDetails

const TicketDetailsSkeleton = () => {
  return (
    <div className='p-6 space-y-6 shadow-md mx-auto'>
      <div className='grid grid-cols-5 gap-4 bg-blue-100 p-4 rounded-md'>
        <div className='col-span-1 space-y-2'>
          <GradiantSkelton width='w-16' height='h-4' />
          <GradiantSkelton width='w-24' height='h-6' />
        </div>
        <div className='col-span-1 space-y-2'>
          <GradiantSkelton width='w-12' height='h-4' />
          <GradiantSkelton width='w-20' height='h-6' />
        </div>
        <div className='col-span-1 space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-28' height='h-6' />
        </div>
        <div className='col-span-2 space-y-2'>
          <GradiantSkelton width='w-32' height='h-4' />
          <GradiantSkelton width='w-48' height='h-6' />
        </div>
      </div>

      <div className='flex gap-12 border-b pb-2'>
        <GradiantSkelton width='w-20' height='h-5' />
        <GradiantSkelton width='w-20' height='h-5' />
      </div>

      <div className='grid grid-cols-2 gap-6'>
        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>
        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>

        <div className='col-span-2 space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>

        <div className='col-span-2 space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-40' />
        </div>

        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>
        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>

        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>
        <div className='space-y-2'>
          <GradiantSkelton width='w-24' height='h-4' />
          <GradiantSkelton width='w-full' height='h-11' />
        </div>
      </div>
    </div>
  )
}
